import React from "react";
import { CircularProgress } from "@nextui-org/react";
import { useTranslation } from "react-i18next";
import { MdOutlineCallMade, MdOutlineCallReceived, MdOutlineSwapVert } from "react-icons/md";

// Two-value KPI card: incoming vs outgoing call counts, with a split share bar.
// Mirrors KpiStatCard's layout so both sit on the same dashboard row.
const DIRECTIONS = [
  {
    key: "incoming",
    label: "Incoming Calls",
    icon: MdOutlineCallReceived,
    text: "text-primary",
    fill: "bg-primary",
  },
  {
    key: "outgoing",
    label: "Outgoing Calls",
    icon: MdOutlineCallMade,
    text: "text-info",
    fill: "bg-info",
  },
];

const CallDirectionCard = ({ incoming = 0, outgoing = 0, loading = false, onSelect = null }) => {
  const { t } = useTranslation();

  const counts = { incoming, outgoing };
  const total = incoming + outgoing;
  const noData = total === 0;
  // Complement the second share so the two always add up to 100%.
  const incomingPct = total > 0 ? Math.round((incoming / total) * 100) : 0;
  const shares = { incoming: incomingPct, outgoing: total > 0 ? 100 - incomingPct : 0 };
  const isClickable = typeof onSelect === "function";

  return (
    <div className="bg-white rounded-md px-[24px] py-[20px] shadow-custom-light h-full flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-full flex items-center justify-center bg-primary/10">
          <MdOutlineSwapVert
            className="text-lg text-primary"
            size={20}
          />
        </div>
        <span className="text-sm text-gray-500">{t("Incoming / Outgoing")}</span>
      </div>

      {loading ? (
        <CircularProgress
          aria-label="Loading..."
          color="primary"
          size="sm"
        />
      ) : (
        <div className="flex items-end justify-between gap-4">
          {DIRECTIONS.map((direction) => {
            const Icon = direction.icon;
            return (
              <button
                type="button"
                key={direction.key}
                onClick={() => isClickable && onSelect(direction.key)}
                disabled={!isClickable || noData}
                className={`text-left group ${
                  isClickable && !noData ? "cursor-pointer" : "cursor-default"
                }`}
              >
                <div className="flex items-center gap-1.5 text-xs text-gray-400">
                  <Icon
                    className={noData ? "text-gray-300" : direction.text}
                    size={14}
                  />
                  <span className="whitespace-nowrap">{t(direction.label)}</span>
                </div>
                <div
                  className={`text-[28px] leading-none font-semibold mt-2 transition-opacity ${
                    noData ? "text-gray-300" : `${direction.text} group-hover:opacity-80`
                  }`}
                >
                  {noData ? t("N/A") : counts[direction.key].toLocaleString("fr-FR")}
                </div>
              </button>
            );
          })}
        </div>
      )}

      <div className="mt-auto">
        <div className="w-full h-2 rounded-full bg-gray-100 overflow-hidden flex">
          {!noData &&
            DIRECTIONS.map((direction) => (
              <div
                key={direction.key}
                className={`h-full ${direction.fill} transition-all`}
                style={{ width: `${shares[direction.key]}%` }}
              />
            ))}
        </div>
        <div className="text-xs text-gray-400 mt-2">
          {noData
            ? t("No data")
            : `${t("Incoming")} ${shares.incoming}% · ${t("Outgoing")} ${shares.outgoing}%`}
        </div>
      </div>
    </div>
  );
};

export default CallDirectionCard;
